import { Input } from '@cademy-ui/form';
import { FunctionComponent, useMemo, SetStateAction, useCallback, ChangeEvent } from 'react';
import styles from './index.module.scss';
import Button from '../../../../../Button';
import { PlusSolidIcon } from '../../../../../Icons';
import { Temporal } from 'temporal-polyfill';

const indexToLabelMap: { [key: number]: string } = {
    0: 'First',
    1: 'Second',
    2: 'Third',
};

type AddDateButtonProps = {
    dates: string[];
    onClick: () => void;
};
const AddDateButton: FunctionComponent<AddDateButtonProps> = ({ dates, onClick }) => {
    if (dates.length >= 3) return null;

    return (
        <div className={styles.addDate}>
            <Button onClick={onClick}>
                <PlusSolidIcon />
                {`Add ${indexToLabelMap[dates.length]} Choice`}
            </Button>
        </div>
    );
};

type DateInputProps = {
    value: string;
    label: string;
    disableRemoveButton: boolean;
    onChange: (date: string) => void;
    onRemove: () => void;
};
const DateInput: FunctionComponent<DateInputProps> = ({
    label,
    value,
    disableRemoveButton,
    onChange,
    onRemove,
}) => {
    const plainValue = useMemo(() => {
        if (value === '') {
            return '';
        }
        return Temporal.ZonedDateTime.from(value).toPlainDate().toString();
    }, [value]);

    const handleChange = useCallback(
        (newValue: string | null) => {
            if (newValue === null) {
                return onChange('');
            }
            const zonedDateTime = Temporal.PlainDate.from(newValue).toZonedDateTime(
                Temporal.Now.timeZoneId()
            );
            onChange(zonedDateTime.toString());
        },
        [onChange]
    );

    return (
        <div className={styles.dateChoice}>
            <Input
                label={label}
                name="date"
                type="date"
                value={plainValue}
                onChange={handleChange}
                required
            />
            <Button disabled={disableRemoveButton} onClick={onRemove}>
                Remove
            </Button>
        </div>
    );
};

export type DatesInputsProps = {
    dates: string[];
    setDates: (dates: SetStateAction<Array<string>>) => void;
};
export const DateInputs: FunctionComponent<DatesInputsProps> = ({ dates, setDates }) => {
    const dateInputs = useMemo(() => {
        return dates.map((date, index) => {
            return (
                <DateInput
                    key={index}
                    label={`${indexToLabelMap[index]} choice`}
                    value={date}
                    disableRemoveButton={dates.length === 1}
                    onChange={(newValue) => {
                        setDates(
                            dates.map((currentDate, i) => {
                                if (i === index) return newValue;
                                return currentDate;
                            })
                        );
                    }}
                    onRemove={() => {
                        if (dates.length === 1) return;
                        setDates(dates.filter((_, i) => i !== index));
                    }}
                />
            );
        });
    }, [dates, setDates]);

    const addNewDate = useCallback(() => {
        setDates((dates) => [...dates, '']);
    }, [setDates]);

    return (
        <div>
            <h2>Preferred Dates</h2>
            {dateInputs}
            <AddDateButton dates={dates} onClick={addNewDate} />
        </div>
    );
};
